/**
 * Cross-browser "unread" surfacing that works while the user is focused on
 * another tab or window. Three independent, gracefully-degrading layers, all
 * driven by the app-wide unread total (see `useUnreadIndicator`):
 *
 *  1. Tab title    — prefixes `document.title` with `(N)`. No permission,
 *                    works in every browser. The primary, always-on signal.
 *  2. Favicon dot  — overlays a red badge on the favicon via a canvas raster.
 *                    No permission, works everywhere with a 2D canvas; a no-op
 *                    where canvas is unavailable.
 *  3. Flash        — a transient OS notification (which also flashes the
 *                    taskbar/dock on most OSes) when the total rises while the
 *                    user is focused elsewhere. Fires ONLY if the user has
 *                    already granted notification permission — it never prompts.
 *
 * The Badging API (`navigator.setAppBadge`) is deliberately NOT used here: it
 * is Chromium-only and requires an installed PWA, so it is not cross-browser.
 */

const DEFAULT_MAX = 99;
const TITLE_PREFIX_RE = /^\(\d+\+?\)\s+/;

/** `42` → `"42"`, `150` → `"99+"`. */
export function formatBadgeCount(count: number, max = DEFAULT_MAX): string {
  return count > max ? `${max}+` : String(Math.max(0, Math.trunc(count)));
}

/** Remove a leading `(N)` / `(99+)` badge we previously added. */
export function stripTitleBadge(title: string): string {
  return title.replace(TITLE_PREFIX_RE, '');
}

/**
 * Compose the badged title from a base title. Re-derives the base by stripping
 * any badge we already added, so it is idempotent across re-renders and
 * composes with route-level titles that may be set later.
 */
export function buildBadgedTitle(currentTitle: string, count: number, max = DEFAULT_MAX): string {
  const base = stripTitleBadge(currentTitle);
  return count > 0 ? `(${formatBadgeCount(count, max)}) ${base}` : base;
}

// --- Favicon -------------------------------------------------------------

const BASE_FAVICON_SRC = '/favicon-32x32.png';
// Badge palette. Kept as literals because a 2D canvas needs concrete colors and
// cannot read CSS custom properties; `BADGE_FILL` mirrors Tailwind `red-500`
// (`bg-red-500`) used by the in-page `NotificationBadge`.
const BADGE_FILL = '#ef4444';
const BADGE_RING = '#0a0a0a';
const BADGE_TEXT = '#ffffff';
// Module-level singletons: the hook mounts exactly once at the app root.
let originalIcons: Array<{ el: HTMLLinkElement; href: string | null; type: string | null }> | null =
  null;
let baseFaviconImage: HTMLImageElement | null = null;

function iconLinks(): HTMLLinkElement[] {
  return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
}

/**
 * Point every `<link rel="icon">` at a badge raster, remembering the originals
 * once so `clearFaviconBadge` can restore them. Overwriting the existing links
 * (rather than appending one) is the reliable cross-browser approach: it also
 * overrides an SVG favicon that browsers would otherwise prefer.
 *
 * Exported as the testable DOM seam: the canvas rendering above can't run under
 * jsdom (no 2D context / image load), but this link-mutation + restore logic is
 * where the real correctness risk lives, so it's covered directly.
 */
export function applyFaviconBadgeHref(dataUrl: string): void {
  const links = iconLinks();
  if (links.length === 0) return;
  if (originalIcons === null) {
    originalIcons = links.map((el) => ({
      el,
      href: el.getAttribute('href'),
      type: el.getAttribute('type'),
    }));
  }
  for (const el of links) {
    el.setAttribute('href', dataUrl);
    el.setAttribute('type', 'image/png');
  }
}

function renderBadgedFavicon(count: number, max: number): void {
  const draw = (img: HTMLImageElement | null): void => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // No 2D canvas (e.g. jsdom) — degrade to title-only.
    if (img) ctx.drawImage(img, 0, 0, 32, 32);

    // Red dot in the top-right, ringed for contrast against any backdrop.
    ctx.beginPath();
    ctx.arc(23, 9, 9, 0, 2 * Math.PI);
    ctx.fillStyle = BADGE_FILL;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = BADGE_RING;
    ctx.stroke();

    if (count <= max) {
      ctx.fillStyle = BADGE_TEXT;
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(count), 23, 10);
    }

    try {
      applyFaviconBadgeHref(canvas.toDataURL('image/png'));
    } catch {
      // toDataURL can throw on a tainted canvas; favicon stays unbadged.
    }
  };

  if (baseFaviconImage) {
    draw(baseFaviconImage);
    return;
  }
  const img = new Image();
  img.onload = () => {
    baseFaviconImage = img;
    draw(img);
  };
  img.onerror = () => draw(null); // Draw the dot on a bare canvas if load fails.
  img.src = BASE_FAVICON_SRC;
}

/** Overlay a red count badge on the favicon. Clears it when `count <= 0`. */
export function setFaviconBadge(count: number, max = DEFAULT_MAX): void {
  if (typeof document === 'undefined') return;
  if (count <= 0) {
    clearFaviconBadge();
    return;
  }
  renderBadgedFavicon(count, max);
}

/** Restore the favicon links to their original hrefs/types. */
export function clearFaviconBadge(): void {
  if (!originalIcons) return;
  for (const { el, href, type } of originalIcons) {
    if (href !== null) el.setAttribute('href', href);
    if (type !== null) el.setAttribute('type', type);
    else el.removeAttribute('type');
  }
  originalIcons = null;
}

// --- Flash (transient OS notification) -----------------------------------

/**
 * Fire a transient notification (which also flashes the taskbar/dock on most
 * OSes) summarising the unread total. Cross-browser, but intentionally
 * conservative:
 *  - never prompts — fires only when permission is ALREADY `granted`
 *    (consent is requested in user-initiated surfaces such as chat settings);
 *  - the caller is expected to fire only when the count rises and Fias is not
 *    focused, so we don't pop a toast while the user is looking at the page.
 *
 * Returns whether a notification was actually shown (for tests/telemetry).
 */
export function flashUnread(count: number, max = DEFAULT_MAX): boolean {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;
  try {
    const notification = new Notification('Fias', {
      body: `You have ${formatBadgeCount(count, max)} unread notification${count === 1 ? '' : 's'}`,
      icon: BASE_FAVICON_SRC,
      // `tag` collapses repeats into a single toast; `renotify` asks to re-alert
      // on each increase. The cast is because `renotify` is missing from some
      // lib.dom versions, and note it's only honoured for persistent (service-
      // worker) notifications in some browsers — harmless to pass here, it's a
      // best-effort enhancement, not relied upon.
      tag: 'fias-unread',
      renotify: true,
    } as NotificationOptions);
    notification.onclick = () => {
      try {
        window.focus();
      } catch {
        // focus may be blocked; closing is enough
      }
      notification.close();
    };
    return true;
  } catch {
    return false; // Notification construction can throw in some contexts
  }
}

/** Test-only: reset module singletons between cases. */
export function __resetUnreadIndicatorState(): void {
  originalIcons = null;
  baseFaviconImage = null;
}
